/**
 * Diff Utilities for Version Comparison
 *
 * This module provides utilities for comparing text content and generating
 * highlighted diff output.
 *
 * Key functions:
 * - computeDiff: Word-level diff for readable change highlighting
 * - hasActualTextChanges: Check if two texts have meaningful differences
 * - extractTextForDiff: Extract plain text from XML/HTML for comparison
 */

import * as Diff from 'diff';

// ============================================================================
// Types
// ============================================================================

export interface DiffPart {
  /** The text content */
  text: string;
  /** Type of change */
  type: 'unchanged' | 'added' | 'removed';
}

export interface DiffResult {
  /** Parts for the old/previous content */
  oldParts: DiffPart[];
  /** Parts for the new/current content */
  newParts: DiffPart[];
  /** Whether there are any actual changes */
  hasChanges: boolean;
  /** Count of removed segments */
  removedCount: number;
  /** Count of added segments */
  addedCount: number;
}

// ============================================================================
// Text Extraction
// ============================================================================

/**
 * Unescape HTML entities
 */
function unescapeHtml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Extract plain text from XML/HTML content for diffing.
 * This performs LIGHT normalization - preserves most formatting
 * so that the diff display shows the real content.
 *
 * @param content - The XML/HTML content
 * @returns The extracted plain text
 */
export function extractTextForDiff(content: string): string {
  if (!content) return '';

  let text = content;

  // Unescape HTML entities first
  text = unescapeHtml(text);

  // Replace block-level tags with newlines to preserve structure
  text = text.replace(/<\/(p|paragraph|div|tr|li|item)>/gi, '\n');
  text = text.replace(/<(br|hr)\s*\/?>/gi, '\n');

  // Replace table cells with tabs for alignment
  text = text.replace(/<\/(td|th)>/gi, '\t');

  // Strip all remaining HTML/XML tags
  text = text.replace(/<[^>]+>/g, '');

  // Remove zero-width characters that can cause invisible differences
  text = text.replace(/[\u200b-\u200d\ufeff\u00ad]/g, '');

  // Normalize whitespace but keep structure
  text = text.replace(/[\t\r\f\v]+/g, ' ');
  text = text.replace(/ +/g, ' ');
  text = text.replace(/\n +/g, '\n');
  text = text.replace(/ +\n/g, '\n');
  text = text.replace(/\n\n+/g, '\n\n');

  // Trim
  text = text.trim();

  return text;
}

/**
 * Normalize text for COMPARISON purposes only (aggressive).
 * This is used to determine if sections are "Modified" vs "Unchanged".
 * Strips all punctuation to ignore FDA formatting inconsistencies.
 */
export function normalizeForComparison(content: string): string {
  if (!content) return '';

  let text = extractTextForDiff(content);

  // Aggressive normalization - lowercase and keep only alphanumeric
  text = text.toLowerCase();
  text = text.replace(/[^a-z0-9\s]/g, ' ');
  text = text.replace(/\s+/g, ' ');
  text = text.trim();

  return text;
}

/**
 * Check if two content strings have actual text differences
 * Uses aggressive normalization to ignore formatting-only differences.
 */
export function hasActualTextChanges(oldContent: string, newContent: string): boolean {
  const oldText = normalizeForComparison(oldContent || '');
  const newText = normalizeForComparison(newContent || '');
  return oldText !== newText;
}

// ============================================================================
// Diff Computation
// ============================================================================

/**
 * Compute word-level diff and generate parts for rendering.
 * Uses diffWords for better readability while still catching meaningful changes.
 *
 * @param oldContent - The old/previous XML/HTML content
 * @param newContent - The new/current XML/HTML content
 * @returns DiffResult with parts for rendering
 */
export function computeDiff(oldContent: string, newContent: string): DiffResult {
  const oldText = extractTextForDiff(oldContent || '');
  const newText = extractTextForDiff(newContent || '');

  // Use word-level diff for better readability
  const diffResult = Diff.diffWords(oldText, newText);

  const oldParts: DiffPart[] = [];
  const newParts: DiffPart[] = [];

  let removedCount = 0;
  let addedCount = 0;

  for (const part of diffResult) {
    if (part.removed) {
      oldParts.push({ text: part.value, type: 'removed' });
      removedCount++;
    } else if (part.added) {
      newParts.push({ text: part.value, type: 'added' });
      addedCount++;
    } else {
      // Unchanged - add to both
      oldParts.push({ text: part.value, type: 'unchanged' });
      newParts.push({ text: part.value, type: 'unchanged' });
    }
  }

  return {
    oldParts,
    newParts,
    hasChanges: removedCount > 0 || addedCount > 0,
    removedCount,
    addedCount,
  };
}

/**
 * Compute character-level diff for more precise highlighting.
 * Use this when you need to show exact character differences.
 *
 * @param oldContent - The old/previous XML/HTML content
 * @param newContent - The new/current XML/HTML content
 * @returns DiffResult with parts for rendering
 */
export function computeCharDiff(oldContent: string, newContent: string): DiffResult {
  const oldText = extractTextForDiff(oldContent || '');
  const newText = extractTextForDiff(newContent || '');

  const diffResult = Diff.diffChars(oldText, newText);

  const oldParts: DiffPart[] = [];
  const newParts: DiffPart[] = [];

  let removedCount = 0;
  let addedCount = 0;

  for (const part of diffResult) {
    if (part.removed) {
      oldParts.push({ text: part.value, type: 'removed' });
      removedCount++;
    } else if (part.added) {
      newParts.push({ text: part.value, type: 'added' });
      addedCount++;
    } else {
      oldParts.push({ text: part.value, type: 'unchanged' });
      newParts.push({ text: part.value, type: 'unchanged' });
    }
  }

  return {
    oldParts,
    newParts,
    hasChanges: removedCount > 0 || addedCount > 0,
    removedCount,
    addedCount,
  };
}

// ============================================================================
// Legacy exports for compatibility
// ============================================================================

export function extractTextFromHtml(html: string): string {
  return extractTextForDiff(html);
}
